import React from 'react';
import { useTranslation } from 'react-i18next';
import { Sparkles, FileText } from 'lucide-react';

/**
 * MetadataStepShell
 * Reusable layout shell for Step 1 (Metadata)
 * 
 * Props:
 * - showPreview: boolean - whether to show the two-column layout
 * - metadata: object - metadata to display in preview
 * - headerTitle: string - main header title
 * - headerSubtitle: string - header subtitle text
 * - onEdit: function - handler for inline editing (path, value)
 * - children: React node - chat panel/additional content
 */
export default function MetadataStepShell({ 
  showPreview = false, 
  metadata = {}, 
  headerTitle,
  headerSubtitle,
  onEdit = null,
  children 
}) {
  const { t } = useTranslation();
  const defaultHeaderTitle = headerTitle || t('step1.tellAboutCourse');
  const defaultHeaderSubtitle = headerSubtitle || t('step1.chatToGetStarted');
  return (
    <div className="w-full" data-testid="metadata-step-shell">
      {/* Initial centered view - no preview */}
      {!showPreview && (
        <div className="text-center animate-fadeIn">
        </div>
      )}

      {/* Preview view - shows course details */}
      {showPreview && (
        <div className="h-full px-[1px] py-8 animate-slideInFromRight">
          <div className="bg-white p-8 rounded-2xl shadow-xl border-2 border-orange-100 mb-6 animate-scaleIn">
            <div className="flex items-center gap-2 mb-6">
              <FileText className="text-orange-600" size={28} />
              <h3 className="text-2xl font-bold text-gray-800">{t('step1.coursePreview')}</h3>
            </div>

            <div className="flex flex-col gap-4">
              {metadata.title && (
                <div className="animate-fadeIn">
                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-widest mb-2">{t('step1.title')}</h4>
                  <p 
                    className="text-2xl font-bold text-gray-900 leading-tight hover:bg-gray-50 px-2 py-1 rounded transition-colors cursor-text"
                    contentEditable={!!onEdit}
                    suppressContentEditableWarning
                    onBlur={onEdit ? (e) => onEdit('title', e.target.textContent) : undefined}
                  >
                    {metadata.title}
                  </p>
                </div>
              )}

              <div className="animate-fadeIn rounded-xl bg-orange-50/60 border border-orange-100 p-4" style={{animationDelay: '0.1s'}}>
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-widest mb-1">{t('step1.totalHours')}</h4>
                <p 
                  className="text-lg text-teal-600 font-semibold hover:bg-white px-2 py-1 rounded transition-colors cursor-text"
                  contentEditable={!!onEdit}
                  suppressContentEditableWarning
                  onBlur={onEdit ? (e) => {
                    const value = parseInt(e.target.textContent.replace(/[^\d]/g, '')) || 0;
                    onEdit('totalHours', value);
                  } : undefined}
                >
                  {metadata.totalHours > 0 ? `${metadata.totalHours} ${t('progressBar.hours')}` : '-'}
                </p>
              </div>

              {metadata.educationalLevel && (
                <div className="animate-fadeIn rounded-xl bg-orange-50/60 border border-orange-100 p-4" style={{animationDelay: '0.2s'}}>
                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-widest mb-1">{t('step1.educationalLevel')}</h4>
                  <p 
                    className="text-xl text-orange-600 font-semibold hover:bg-white px-2 py-1 rounded transition-colors cursor-text"
                    contentEditable={!!onEdit}
                    suppressContentEditableWarning
                    onBlur={onEdit ? (e) => onEdit('educationalLevel', e.target.textContent) : undefined}
                  >
                    {metadata.educationalLevel}
                  </p>
                </div>
              )}

              {metadata.grade && (
                <div className="animate-fadeIn rounded-xl bg-orange-50/60 border border-orange-100 p-4" style={{animationDelay: '0.3s'}}>
                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-widest mb-1">{t('step1.grade')}</h4>
                  <p 
                    className="text-xl text-blue-600 font-semibold hover:bg-white px-2 py-1 rounded transition-colors cursor-text"
                    contentEditable={!!onEdit}
                    suppressContentEditableWarning
                    onBlur={onEdit ? (e) => onEdit('grade', e.target.textContent) : undefined}
                  >
                    {metadata.grade}
                  </p>
                </div>
              )}

              {metadata.subjectArea && (
                <div className="animate-fadeIn rounded-xl bg-orange-50/60 border border-orange-100 p-4" style={{animationDelay: '0.4s'}}>
                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-widest mb-1">{t('step1.subjectArea')}</h4>
                  <p 
                    className="text-lg text-purple-600 font-semibold hover:bg-white px-2 py-1 rounded transition-colors cursor-text"
                    contentEditable={!!onEdit}
                    suppressContentEditableWarning
                    onBlur={onEdit ? (e) => onEdit('subjectArea', e.target.textContent) : undefined}
                  >
                    {metadata.subjectArea}
                  </p>
                </div>
              )}

              {metadata.description && metadata.description.trim() !== '' && (
                <div className="animate-fadeIn rounded-xl border border-gray-200 p-5 bg-gray-50" style={{animationDelay: '0.5s'}}>
                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-widest mb-2">{t('step3.description')}</h4>
                  <p 
                    className="text-base text-gray-700 leading-relaxed max-h-52 overflow-y-auto pr-1 hover:bg-white px-2 py-1 rounded transition-colors cursor-text"
                    contentEditable={!!onEdit}
                    suppressContentEditableWarning
                    onBlur={onEdit ? (e) => onEdit('description', e.target.textContent) : undefined}
                  >
                    {metadata.description}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Additional content (chat panel, etc.) */}
          {children}
        </div>
      )}
    </div>
  );
}


